import { useState } from "react";
import { LogIn, LogOut, User } from "lucide-react";
import { nip19 } from "nostr-tools";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useLoginActions } from "@/hooks/useLoginActions";
import { LoginDialog } from "@/components/auth/LoginDialog";

/**
 * Pop-art styled login control. Renders a "Log in" button when logged
 * out, and an account chip + logout when logged in. Modeled on the
 * treasures.to LoginArea.
 */
export function LoginArea() {
  const { user, metadata } = useCurrentUser();
  const login = useLoginActions();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (user) {
    const npub = nip19.npubEncode(user.pubkey);
    const name = metadata?.name || `${npub.slice(0, 12)}…`;
    return (
      <div className="flex items-center gap-2">
        <div
          className="flex items-center gap-2 rounded-full border-2 border-black bg-white px-3 py-1.5 shadow-[2px_2px_0_0_#000]"
          title={npub}
        >
          {metadata?.picture ? (
            <img
              src={metadata.picture}
              alt=""
              className="h-6 w-6 rounded-full border border-black object-cover"
            />
          ) : (
            <span className="flex h-6 w-6 items-center justify-center rounded-full border border-black bg-[#ffd23f]">
              <User className="h-3.5 w-3.5" />
            </span>
          )}
          <span className="max-w-[10rem] truncate text-sm font-bold text-[#3d1a5e]">
            {name}
          </span>
        </div>
        <button
          onClick={() => login.logout()}
          className="flex items-center gap-1 rounded-full border-2 border-black bg-[#ff3ea5] px-3 py-1.5 text-xs font-black uppercase text-white shadow-[2px_2px_0_0_#000] transition-transform hover:scale-105 active:scale-95"
        >
          <LogOut className="h-3.5 w-3.5" /> Out
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setDialogOpen(true)}
        className="flex items-center gap-2 rounded-full border-2 border-black px-5 py-2.5 text-sm font-black uppercase tracking-wide text-white shadow-[3px_3px_0_0_#000] transition-transform hover:scale-105 active:scale-95"
        style={{
          background: "linear-gradient(135deg, #3ec1ff 0%, #a26dff 100%)",
        }}
      >
        <LogIn className="h-4 w-4" /> Log in
      </button>
      <LoginDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onLogin={() => setDialogOpen(false)}
      />
    </>
  );
}
